// LoginPage.js (גרסה מתוקנת עם ניווט לנהג)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getAuth,
    signInWithPopup,
    GoogleAuthProvider,
    FacebookAuthProvider,
    RecaptchaVerifier,
    signInWithPhoneNumber
} from "firebase/auth";
import { doc, getDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import LoadingAnimation from './components/LoadingAnimation';

console.log('[DEBUG] LoginPage loaded');

function LoginPage() {
    const auth = getAuth();
    const navigate = useNavigate();
    const [phone, setPhone] = useState("");
    const [code, setCode] = useState("");
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [phoneStep, setPhoneStep] = useState(0);
    const [shops, setShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(true);

    useEffect(() => {
        if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier('recaptcha-container', { size: 'invisible' }, auth);
            window.recaptchaVerifier.render();
        }
        return () => {
            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear();
                window.recaptchaVerifier = null;
            }
        };
    }, [auth]);

    useEffect(() => {
        async function fetchShops() {
            setLoadingShops(true);
            try {
                const usersSnapshot = await getDocs(collection(db, "users"));
                const shopsList = usersSnapshot.docs.map(docSnap => {
                    const data = docSnap.data();
                    return {
                        id: docSnap.id,
                        name: data.name || data.displayName || data.email || docSnap.id
                    };
                });
                setShops(shopsList);
            } catch (e) {
                setShops([]);
            }
            setLoadingShops(false);
        }
        fetchShops();
    }, []);

    const setupNewUser = async (user) => {
        try {
            console.log("[DEBUG] setupNewUser called for:", user.uid);
            const userDocRef = doc(db, "users", user.uid);
            const docSnap = await getDoc(userDocRef);

            if (!docSnap.exists()) {
                console.log("New user detected! Creating documents...");
                const newUserDoc = {
                    name: user.displayName,
                    email: user.email,
                    createdAt: new Date(),
                    role: 'driver', // הוספת תפקיד ברירת מחדל
                    status: { isOpen: true, reopenMessage: "החנות סגורה כרגע." },
                    display: { showPots: true }
                };
                await setDoc(userDocRef, newUserDoc);

                // יצירת תתי-אוספים
                await setDoc(doc(db, "users", user.uid, "pots", "mehfood"), { currentLevelLiters: 40 });
                await setDoc(doc(db, "users", user.uid, "pots", "sheeritYisrael"), { currentLevelLiters: 40 });
                await setDoc(doc(db, "users", user.uid, "queue", "last"), { value: 0 });

                console.log("User documents created successfully!");
                return newUserDoc; // מחזירים את המידע של המשתמש החדש
            } else {
                console.log("Returning user. No need to create documents.");
                return docSnap.data(); // מחזירים את המידע של המשתמש הקיים
            }
        } catch (error) {
            console.error("[DEBUG] setupNewUser error:", error);
            return null;
        }
    };

    const handleLoginAndNavigate = async (loginProvider) => {
        try {
            const result = await signInWithPopup(auth, loginProvider);
            const user = result.user;
            console.log("[DEBUG] Login success:", user);
            
            const userData = await setupNewUser(user);

            // לוגיקת ניווט
            if (userData && userData.role === 'driver') {
                console.log("Navigating to /driver...");
                navigate('/driver');
            } else {
                console.log("User is not a driver or data is missing, navigating to home.");
                navigate('/');
            }
        } catch (error) {
            console.error("[DEBUG] Login error:", error);
            alert("ההתחברות נכשלה. נסה שוב.");
        }
    };

    const handleGoogleLogin = () => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });
        handleLoginAndNavigate(provider);
    };

    const handleFacebookLogin = () => {
        const provider = new FacebookAuthProvider();
        handleLoginAndNavigate(provider);
    };

    const handleSendCode = async () => {
        if (!phone) return alert("יש להזין מספר טלפון");
        try {
            const result = await signInWithPhoneNumber(auth, phone, window.recaptchaVerifier);
            setConfirmationResult(result);
            setPhoneStep(1);
            alert("קוד נשלח!");
        } catch (error) {
            alert("שגיאה בשליחת קוד: " + error.message);
        }
    };

    const handleVerifyCode = async () => {
        if (!code) return alert("יש להזין קוד");
        try {
            const result = await confirmationResult.confirm(code);
            const userData = await setupNewUser(result.user);
            alert("התחברת בהצלחה!");
            
            if (userData && userData.role === 'driver') {
                navigate("/driver");
            } else {
                navigate("/");
            }
        } catch (error) {
            alert("קוד לא נכון: " + error.message);
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
            {loadingShops && <LoadingAnimation message="טוען חנויות..." />}
            <div className="login-background">
                <div className="login-card" dir="rtl">
                    <img src="/Logo.jpg" alt="Choltiya Logo" className="login-logo" />
                    <h1 className="login-title">ברוכים הבאים</h1>
                    <p className="login-subtitle">כדי לנהל, יש להתחבר למערכת</p>
                    <button className="login-btn login-btn-google" onClick={handleGoogleLogin}>התחבר עם גוגל</button>
                    <button className="login-btn login-btn-facebook" onClick={handleFacebookLogin}>התחבר עם פייסבוק</button>
                    <div className="login-divider"><span>או</span></div>
                    <div className="login-phone-box">
                        <h3 className="login-phone-title">התחברות עם מספר טלפון</h3>
                        {phoneStep === 0 && (
                            <>
                                <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="מספר טלפון בפורמט +972..." className="login-input" style={{direction: 'ltr'}} />
                                <button className="login-btn login-btn-phone" onClick={handleSendCode}>שלח קוד</button>
                                <div id="recaptcha-container"></div>
                            </>
                        )}
                        {phoneStep === 1 && (
                            <>
                                <input value={code} onChange={e => setCode(e.target.value)} placeholder="קוד SMS" className="login-input" style={{direction: 'ltr'}} />
                                <button className="login-btn login-btn-phone" onClick={handleVerifyCode}>אמת קוד</button>
                            </>
                        )}
                    </div>
                    <div className="login-customer-list" style={{marginTop: 32}}>
                        <h3 className="login-phone-title" style={{marginBottom: 12}}>היכנס כלקוח לאחת החנויות:</h3>
                        {loadingShops ? (
                            <div style={{color: '#888', textAlign: 'center'}}>טוען חנויות...</div>
                        ) : (
                            <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                                {shops.map(shop => (
                                    <li key={shop.id} style={{marginBottom: 8}}>
                                        <button
                                            className="login-btn login-btn-customer"
                                            style={{width: '100%', background: '#f3f4f6', color: '#222', border: '1px solid #ddd', borderRadius: 8, padding: 12, fontWeight: 600, cursor: 'pointer'}}
                                            onClick={() => navigate(`/shop/${shop.id}`)}
                                        >
                                            {shop.name}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LoginPage;